import { AllCardsType } from '../../../data/all-cards'
import { NombreTarotista } from '../../../data/personality'
import { ComboWizardData } from '../shared/combo-wizard-props'
import { buildComboPreamble, ComboPreviousStep } from '../../../utils/combo-context'
import { formatCardLabel } from '../../../utils/card-label'
import {
    SANACION_PROFUNDA_NAME,
    SANACION_PROFUNDA_PREAMBLE,
    SANACION_PROFUNDA_STEPS,
    SANACION_PROFUNDA_TOTAL_STEPS,
} from './combo-config'

const cardLabel = (card: AllCardsType) => formatCardLabel(card)

const consultaExtra = (data: ComboWizardData) =>
    data.consulta.length > 0
        ? 'ten en cuenta en la tirada la siguiente consulta que realiza el consultante: ' +
          data.consulta +
          ','
        : ''

const buildStepPrompt = (step: number, cards: AllCardsType[], data: ComboWizardData): string => {
    const extra = consultaExtra(data)

    if (step === 1) {
        return `Quiero que interpretes una **Tirada de Sanación del Niño Interior** con 10 cartas, ayudando al consultante ${
            data.consultante
        } a conectar con su niño interior, comprender sus heridas y abrir un camino de sanación y reparenting, ${extra} según estas cartas: **Estado actual de tu niño interior (¿cómo está tu niño interior hoy?):** ${cardLabel(
            cards[0]
        )}, **La herida principal que carga (¿cuál es la herida más fuerte que sigue presente?):** ${cardLabel(
            cards[1]
        )}, **Cómo se manifiesta en tu vida adulta (¿de qué forma aparece esta herida hoy?):** ${cardLabel(
            cards[2]
        )}, **Qué necesitó y no recibió (¿qué necesitaba ese niño y no obtuvo?):** ${cardLabel(
            cards[3]
        )}, **Creencia que se formó (¿qué creencia limitante se generó a partir de esa herida?):** ${cardLabel(
            cards[4]
        )}, **Cómo te limita o sabotea hoy (¿de qué forma te está afectando actualmente?):** ${cardLabel(
            cards[5]
        )}, **Qué necesita tu niño interior ahora (¿qué necesita recibir de vos en este momento?):** ${cardLabel(
            cards[6]
        )}, **Cómo podés reparentarlo (¿qué acción concreta podés hacer para sanarlo?):** ${cardLabel(
            cards[7]
        )}, **El regalo que trae la sanación (¿qué fuerza o don te va a dar sanar esta herida?):** ${cardLabel(
            cards[8]
        )} y **Mensaje final de tu niño interior (¿qué quiere decirte tu niño interior ahora?):** ${cardLabel(
            cards[9]
        )}. Explica detalladamente cada carta y su significado en la posición donde ha salido. Teje una lectura coherente sobre la herida del niño interior: conecta el pasado con el presente, la creencia limitante, las necesidades no cubiertas y el camino de sanación. Detecta patrones de autosabotaje, bloqueos emocionales y oportunidades de reparenting. Proporciona consejos prácticos, tiernos y concretos para sanar y cuidar al niño interior. Mantén un tono profesional, empático, contenido y comprensible. Dirígete directamente al consultante, no utilices la tercera persona refiriéndote al consultante, aún cuando las consultas se encuentren en tercera persona, adapta la respuesta para que quede en segunda persona. Incluye además en 2 párrafos después de la lectura, una respuesta directa para el consultante y un consejo final (extra-tarot).`
    }

    if (step === 2) {
        return `Quiero que interpretes una **Tirada de Romper Patrones Repetitivos** con 10 cartas, ayudando al consultante ${
            data.consultante
        } a identificar y liberarse de un ciclo o situación que se repite una y otra vez en su vida, ${extra} según estas cartas: **El patrón que se repite (cuál es el patrón o situación que se repite una y otra vez; patrón identificado):** ${cardLabel(
            cards[0]
        )}, **Origen del patrón (de dónde viene: infancia, familia, vidas pasadas, experiencias; raíz):** ${cardLabel(
            cards[1]
        )}, **Cómo se manifiesta actualmente (de qué forma está apareciendo este patrón en su vida hoy; manifestación actual):** ${cardLabel(
            cards[2]
        )}, **Qué le está dando este patrón / beneficio secundario (qué gana, aunque sea doloroso, al mantener este patrón; beneficio oculto):** ${cardLabel(
            cards[3]
        )}, **Qué le está costando este patrón (qué le está quitando o dañando este ciclo repetitivo; costo real):** ${cardLabel(
            cards[4]
        )}, **La lección del alma detrás del patrón (qué quiere enseñarle su alma a través de este patrón; lección espiritual):** ${cardLabel(
            cards[5]
        )}, **Qué necesita soltar (qué creencia, emoción o comportamiento debe soltar; liberación):** ${cardLabel(
            cards[6]
        )}, **Nueva energía o comportamiento a incorporar (qué nueva forma de actuar o de ser necesita adoptar; nueva energía):** ${cardLabel(
            cards[7]
        )}, **Acción concreta para romper el patrón (qué puede hacer de forma práctica para empezar a salir del ciclo; paso accionable):** ${cardLabel(
            cards[8]
        )} y **Energía que se abre al romper el patrón (qué cambia o qué se libera cuando sale de este patrón; resultado / recompensa):** ${cardLabel(
            cards[9]
        )}. Explica detalladamente cada carta y su significado en la posición donde ha salido. Teje una lectura coherente sobre el ciclo repetitivo: conecta el patrón con su origen, su manifestación actual, el beneficio oculto que lo sostiene y el costo real que paga por mantenerlo. Ayuda a comprender la lección del alma, qué debe soltar, qué nueva energía incorporar y qué paso concreto puede dar ahora. Cierra mostrando qué se abre o se libera al romper el patrón. Detecta creencias, emociones y comportamientos que perpetúan el ciclo. Mantén un tono profesional, empático, claro y comprensible, sin juzgar. Dirígete directamente al consultante, no utilices la tercera persona refiriéndote al consultante, aún cuando las consultas se encuentren en tercera persona, adapta la respuesta para que quede en segunda persona. Incluye además en 2 párrafos después de la lectura, una respuesta directa para el consultante y un consejo final (extra-tarot).`
    }

    return `Quiero que interpretes una **Tirada de Mensaje de tus Guías** con 10 cartas, ayudando al consultante ${
        data.consultante
    } a recibir el mensaje de sus guías espirituales, ángeles o energías de acompañamiento en clave de sanación profunda, ${extra} según estas cartas: **Quién te está guiando ahora — Quién conecta (¿qué guía, ángel o energía se está acercando en este momento?):** ${cardLabel(
        cards[0]
    )}, **El mensaje principal — Mensaje central (¿cuál es el mensaje más importante que quieren darte ahora?):** ${cardLabel(
        cards[1]
    )}, **Qué te están pidiendo soltar — Liberación (¿qué creencia, apego, situación o emoción te están pidiendo soltar?):** ${cardLabel(
        cards[2]
    )}, **Qué te están invitando a activar — Activación (¿qué energía, cualidad o acción te están invitando a incorporar?):** ${cardLabel(
        cards[3]
    )}, **Consejo para el momento actual — Consejo práctico (¿qué te recomiendan hacer o cómo transitar este momento?):** ${cardLabel(
        cards[4]
    )}, **Apoyo o herramienta disponible — Apoyo disponible (¿qué ayuda o recurso espiritual tenés disponible ahora?):** ${cardLabel(
        cards[5]
    )}, **Área que necesitan que mires — Área de atención (¿hay algo que estén queriendo que prestes atención? a veces es una advertencia suave):** ${cardLabel(
        cards[6]
    )}, **Cómo podés conectar más con ellos — Práctica recomendada (¿qué podés hacer para fortalecer tu conexión espiritual?):** ${cardLabel(
        cards[7]
    )}, **Energía que viene si seguís su consejo — Resultado probable (¿qué energía o cambio se abre si seguís este mensaje?):** ${cardLabel(
        cards[8]
    )} y **Mensaje de cierre / bendición — Cierre sanador (¿qué quieren que sepas o recuerdes como cierre?):** ${cardLabel(
        cards[9]
    )}. Explica detalladamente cada carta y su significado en la posición donde ha salido. Teje una lectura coherente como un mensaje canalizado de las guías: conecta quién guía, el mensaje central, lo que hay que soltar y activar, y el cierre con bendición. Mantén un tono espiritual, cálido, respetuoso y claro, sin ser dogmático ni imponer creencias. Detecta señales de apoyo, advertencias suaves y oportunidades de conexión. Proporciona consejos prácticos y espirituales accesibles. Mantén un tono profesional, empático y comprensible. Dirígete directamente al consultante, no utilices la tercera persona refiriéndote al consultante, aún cuando las consultas se encuentren en tercera persona, adapta la respuesta para que quede en segunda persona. Incluye además en 2 párrafos después de la lectura, una respuesta directa para el consultante y un consejo final (extra-tarot).`
}

export const interpretStep = (
    step: number,
    cards: AllCardsType[],
    data: ComboWizardData,
    user: string,
    personality: NombreTarotista,
    previousSteps: ComboPreviousStep[],
    comboId: string
) => {
    const config = SANACION_PROFUNDA_STEPS[step - 1]
    const preamble = buildComboPreamble(
        SANACION_PROFUNDA_NAME,
        step,
        SANACION_PROFUNDA_TOTAL_STEPS,
        previousSteps,
        SANACION_PROFUNDA_PREAMBLE
    )
    const consult = preamble + buildStepPrompt(step, cards, data)

    return {
        consult,
        user,
        personality,
        clientName: data.consultante.name,
        clientPhone: data.consultante.phone || '',
        clientAnswer: data.consulta,
        cards,
        type: config.type,
        comboId,
        comboName: SANACION_PROFUNDA_NAME,
        comboStep: step,
        comboTotalSteps: SANACION_PROFUNDA_TOTAL_STEPS,
    }
}
